/**
 * Old LINE history (the OA Manager export, api/app/history.py) linked to the real customer (api/app/line_link.py,
 * migration 0043): a number the customer typed matches one-to-one → linked by the system; the same name only (or a
 * number shared with others) → a suggestion staff answer "ใช่คนเดียวกันไหม".
 *   GET    /api/v1/conversations/:id/line-history         live LINE chat → linked + suggested · history room → whose
 *   POST   /api/v1/conversations/:id/line-history/:hid    {same} ใช่ / ไม่ใช่ (everyone)
 *   DELETE /api/v1/conversations/:id/line-history/:hid    unlink a wrong link (owner / head)
 * Self-contained like ./fbhistory.ts (no change to the shared client): mock mode answers from the mock db.
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { getApi, useMock } from './index'
import { MockChatApi } from './mock'
import type { ChatMessage, ConversationDetail, ThreadItem } from './types'

export type LinkMethod = 'phone' | 'name' | 'manual'

export interface StaffWho {
  id: string
  name: string
}

/** One imported history room as the customer panel shows it. */
export interface HistoryRoom {
  conversation_id: string
  /** Display name in the export (NULL when LINE wrote "Unknown"). */
  name: string | null
  messages: number
  first_at: string | null
  last_at: string | null
  method: LinkMethod
  /** null on a link = made by the system (the phone matched one person). */
  decided_by: StaffWho | null
  decided_at: string | null
}

export interface HistoryLink {
  live_conversation_id: string | null
  live_name: string | null
  method: LinkMethod
  decided_by: StaffWho | null
  decided_at: string | null
}

export type LineHistoryView =
  | { kind: 'none' }
  | { kind: 'live'; linked: HistoryRoom[]; suggested: HistoryRoom[]; can_unlink: boolean }
  | {
      kind: 'history'
      history: { conversation_id: string; name: string | null; messages: number; first_at: string | null; last_at: string | null }
      link: HistoryLink | null
    }

async function call<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown, timeoutMs = 15000): Promise<T> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  const base = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '')
  try {
    const res = await fetch(new URL(`${base}/api/v1${path}`, window.location.origin).toString(), {
      method,
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
    if (res.status === 401) reportUnauthorized()
    if (!res.ok) {
      let detail = res.statusText
      try {
        const j = (await res.json()) as { detail?: unknown }
        if (typeof j.detail === 'string') detail = j.detail
      } catch {
        /* body not JSON */
      }
      throw new ApiError(res.status, detail || `HTTP ${res.status}`)
    }
    return (await res.json()) as T
  } catch (e) {
    if (e instanceof ApiError) throw e
    if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
    throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
  } finally {
    clearTimeout(timer)
  }
}

export interface LineHistoryApi {
  view(convId: string): Promise<LineHistoryView>
  answer(convId: string, historyId: string, same: boolean): Promise<LineHistoryView>
  unlink(convId: string, historyId: string): Promise<LineHistoryView>
}

const enc = encodeURIComponent
const http: LineHistoryApi = {
  view: (id) => call('GET', `/conversations/${enc(id)}/line-history`),
  answer: (id, hid, same) => call('POST', `/conversations/${enc(id)}/line-history/${enc(hid)}`, { same }),
  unlink: (id, hid) => call('DELETE', `/conversations/${enc(id)}/line-history/${enc(hid)}`),
}

// ------------------------------------------------------------------ mock (VITE_USE_MOCK, screens tests)

const DAY = 86400000
/** คุณมายด์ (mock c102): linked by the system (phone) · คุณเบียร์ (mock c103): a name suggestion. */
const MOCK_LINKED = 'c102'
const MOCK_SUGGESTED = 'c103'

type MockRow = HistoryRoom & { live: string; status: 'confirmed' | 'suggested' | 'rejected' }
let mockRows: MockRow[] | null = null

function iso(ms: number): string {
  return new Date(ms).toISOString()
}

function mockDb(api: MockChatApi): MockRow[] {
  if (mockRows) return mockRows
  const now = api.db.now
  mockRows = [
    { live: MOCK_LINKED, status: 'confirmed', conversation_id: `${MOCK_LINKED}lh`, name: 'มายด์', messages: 214,
      first_at: iso(now - 610 * DAY), last_at: iso(now - 12 * DAY), method: 'phone', decided_by: null,
      decided_at: iso(now - 2 * DAY) },
    { live: MOCK_SUGGESTED, status: 'suggested', conversation_id: `${MOCK_SUGGESTED}lh`, name: 'คุณเบียร์',
      messages: 38, first_at: iso(now - 420 * DAY), last_at: iso(now - 95 * DAY), method: 'name', decided_by: null,
      decided_at: null },
  ]
  return mockRows
}

function mockRole(): string {
  try {
    return localStorage.getItem('cf-chat.mock-role') ?? 'staff'
  } catch {
    return 'staff'
  }
}

function mockView(api: MockChatApi, convId: string): LineHistoryView {
  const rows = mockDb(api)
  const hist = rows.find((r) => r.conversation_id === convId)
  if (hist) {
    const live = api.db.conversations.get(hist.live)
    return {
      kind: 'history',
      history: { conversation_id: hist.conversation_id, name: hist.name, messages: hist.messages, first_at: hist.first_at, last_at: hist.last_at },
      link:
        hist.status === 'confirmed'
          ? { live_conversation_id: hist.live, live_name: live?.contact.name ?? null, method: hist.method, decided_by: hist.decided_by, decided_at: hist.decided_at }
          : null,
    }
  }
  const c = api.db.conversations.get(convId)
  if (!c || c.channel !== 'line' || c.kind !== 'dm' || c.is_history) return { kind: 'none' }
  const mine = rows.filter((r) => r.live === convId)
  const strip = ({ live: _l, status: _s, ...room }: MockRow): HistoryRoom => room
  return {
    kind: 'live',
    linked: mine.filter((r) => r.status === 'confirmed').map(strip),
    suggested: mine.filter((r) => r.status === 'suggested').map(strip),
    can_unlink: ['owner', 'head'].includes(mockRole()),
  }
}

function meWho(api: MockChatApi): StaffWho {
  const me = api.db.team.find((m) => m.id === api.db.meId)
  return { id: api.db.meId, name: me?.name ?? 'พนักงาน' }
}

const mock: LineHistoryApi = {
  async view(convId) {
    await new Promise((r) => setTimeout(r, 120))
    return mockView(getApi() as MockChatApi, convId)
  },
  async answer(convId, hid, same) {
    await new Promise((r) => setTimeout(r, 200))
    const api = getApi() as MockChatApi
    const row = mockDb(api).find((r) => r.live === convId && r.conversation_id === hid)
    if (!row) throw new ApiError(404, 'ไม่พบประวัติแชทเก่านี้ในรายการของลูกค้าคนนี้')
    row.status = same ? 'confirmed' : 'rejected'
    row.decided_by = meWho(api)
    row.decided_at = new Date().toISOString()
    return mockView(api, convId)
  },
  async unlink(convId, hid) {
    await new Promise((r) => setTimeout(r, 200))
    const api = getApi() as MockChatApi
    if (!['owner', 'head'].includes(mockRole())) throw new ApiError(403, 'ยกเลิกการผูกได้เฉพาะเจ้าของและผู้จัดการ')
    const row = mockDb(api).find((r) => r.live === convId && r.conversation_id === hid && r.status === 'confirmed')
    if (!row) throw new ApiError(409, 'ประวัตินี้ไม่ได้ผูกกับลูกค้าคนนี้อยู่แล้ว')
    row.status = 'rejected'
    return mockView(api, convId)
  },
}

/** Tests inject their own. */
let impl: LineHistoryApi | null = null
export function setLineHistoryApi(api: LineHistoryApi | null): void {
  impl = api
}

export function lineHistoryApi(): LineHistoryApi {
  if (impl) return impl
  return useMock && getApi() instanceof MockChatApi ? mock : http
}

/** The live chat a history room was opened from (its bar leads back there when the room is not linked yet). */
const openedFrom = new Map<string, string>()
export function openedFromLive(historyId: string): string | null {
  return openedFrom.get(historyId) ?? null
}

/**
 * Before opening a history room from the panel: remember where it was opened from; in mock mode add the room to the
 * mock db only now (the mock lists stay as they were). The real API already has it.
 */
export function prepareLineHistoryChat(liveId: string, historyId: string): void {
  openedFrom.set(historyId, liveId)
  const api = getApi()
  if (impl || !useMock || !(api instanceof MockChatApi) || api.db.conversations.has(historyId)) return
  const live = api.db.conversations.get(liveId)
  const row = mockDb(api).find((r) => r.conversation_id === historyId)
  if (!live || !row) return
  const detail: ConversationDetail = {
    ...structuredClone(live),
    id: historyId,
    status: 'closed',
    needs_reply: false,
    waiting_since: null,
    unread: 0,
    assigned_staff: null,
    last_message: null,
    last_message_at: null,
    follow_up_due_at: null,
    is_history: true,
    dev_resettable: false,
    ad_source: null,
  }
  api.db.conversations.set(historyId, detail)
  const first = Date.parse(row.first_at ?? iso(api.db.now))
  const last = Date.parse(row.last_at ?? iso(api.db.now))
  const msg = (n: number, at: number, dir: 'in' | 'out', text: string): ThreadItem =>
    ({
      kind: 'message',
      id: `${historyId}m${n}`,
      conversation_id: historyId,
      direction: dir,
      type: 'text',
      text,
      attachments: [],
      at: iso(at),
      author: dir === 'in' ? { kind: 'contact' } : { kind: 'page' },
      read: false,
      send: null,
      slip: null,
    }) as ChatMessage as ThreadItem
  api.db.threads.set(historyId, [
    msg(1, first, 'in', 'สวัสดีค่ะ สนใจแพ็กอาหารคลีน 5 วันค่ะ'),
    msg(2, first + 3 * 60000, 'out', 'สวัสดีค่ะ ขอบคุณที่สนใจนะคะ แพ็ก 5 วันมีแบบ 2 มื้อและ 3 มื้อค่ะ'),
    msg(3, first + 8 * 60000, 'in', 'เอาแบบ 2 มื้อค่ะ ไม่ทานเผ็ดนะคะ'),
    msg(4, last - 6 * 60000, 'in', 'ขอสั่งเหมือนรอบที่แล้วค่ะ'),
    msg(5, last, 'out', 'ได้เลยค่ะ ขอบคุณค่ะ'),
  ])
}
